// =============================================================================
// ROBUST CLIPBOARD UTILITIES - Multiple Fallback Methods
// =============================================================================

interface ClipboardResult {
  success: boolean;
  method: 'modern' | 'fallback' | 'manual';
  error?: string;
}

const copyToClipboard = async (text: string): Promise<ClipboardResult> => {
  // Modern clipboard API
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return { success: true, method: 'modern' };
    } catch (error) {
      console.warn('Modern clipboard failed, trying fallback');
    }
  }
  
  // Fallback method
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.cssText = `
      position: absolute; left: -9999px; top: -9999px;
      opacity: 0; width: 2em; height: 2em;
    `;
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (successful) {
      return { success: true, method: 'fallback' };
    }
  } catch (error) {
    console.warn('Fallback copy failed');
  }
  
  return { success: false, method: 'manual', error: 'Please copy manually - select text and use Ctrl+C' };
};

// SIMPLIFIED: Direct copy with toast feedback only
export const copyWithFeedback = async (
  text: string,
  onSuccess?: (method: string) => void,
  onError?: (error: string) => void
): Promise<void> => {
  try {
    const result = await copyToClipboard(text);
    
    if (result.success) {
      if (onSuccess) onSuccess(result.method);
    } else {
      // NO SECONDARY MODAL - just error callback
      if (onError) onError(result.error || 'Copy failed - please select and copy manually');
    }
  } catch (error) {
    if (onError) onError('Copy failed - please select and copy manually');
  }
};

/**
 * Test clipboard functionality
 */
export const testClipboardSupport = (): Promise<{
  modern: boolean;
  execCommand: boolean;
  secure: boolean;
}> => {
  return new Promise((resolve) => {
    const modern = !!(navigator.clipboard && window.isSecureContext);
    const execCommand = !!(document.queryCommandSupported && document.queryCommandSupported('copy'));
    const secure = window.isSecureContext;
    
    resolve({ modern, execCommand, secure });
  });
}; 